import React, { useEffect, useState, useMemo } from "react";
import { InputField } from "@cakrawalajakarta/shared-components";
// import { Country, State, City } from "country-state-city"; // Library removed
import { useNotification } from "../common/NotificationContext";

export const InputFieldVolunteer = ({
  formData,
  handleChange,
  isFormDisabled = false,
  labelColor,
}) => {
  const { showNotification } = useNotification();
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [rawCountries, setRawCountries] = useState([]);
  const [provinceIds, setProvinceIds] = useState({});
  const [countryMap, setCountryMap] = useState({});
  const [stateMap, setStateMap] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState({
    countries: false,
    provinces: false,
    cities: false,
  });

  const GEO_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/geo`;
  const cache = useMemo(() => new Map(), []);

  const humanize = (str) => {
    if (!str) return "";
    if (str === "nomorIdentitas") return "Nomor Identitas";
    if (str === "motivasiDanPengalaman")
      return "Motivasi dan Pengalaman Volunteer";
    if (str === "ukuranJersey") return "Ukuran Jersey";
    if (str === "linkSertifikat") return "Link Sertifikat";
    if (str === "usernameInstagram") return "Username Instagram";
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const fetchWithCache = async (url, cacheKey, type) => {
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    setLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.geonames) {
        cache.set(cacheKey, data);
        return data;
      } else {
        setErrors((prev) => ({
          ...prev,
          [type]: "Gagal memuat data lokasi. Silakan coba lagi nanti.",
        }));
        showNotification(
          "error",
          "Gagal memuat data lokasi. Silakan coba lagi nanti."
        );
        return null;
      }
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [type]: "Gagal memuat data lokasi. Silakan coba lagi nanti.",
      }));
      showNotification(
        "error",
        "Gagal memuat data lokasi. Silakan coba lagi nanti."
      );
      return null;
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  useEffect(() => {
    fetchWithCache(
      `${GEO_API_BASE}/countries`,
      "countries",
      "countries"
    ).then((data) => {
      if (data?.geonames) {
        const countryList = data.geonames.map((country) => ({
          value: country.countryName,
          label: country.countryName,
        })).sort((a, b) => a.label.localeCompare(b.label));
        setCountries(countryList);
        setRawCountries(data.geonames);
      } else {
        setCountries([]);
        setRawCountries([]);
      }
    });
  }, [cache]);

  useEffect(() => {
    if (formData.negara) {
      setProvinces([]);
      setCities([]);

      if (rawCountries.length > 0) {
        const selectedCountry = rawCountries.find(
          (c) => c.countryName === formData.negara
        );
        if (!selectedCountry) return;
  
        fetchWithCache(
          `${GEO_API_BASE}/children/${selectedCountry.geonameId}?lang=en`,
          `provinces_${selectedCountry.geonameId}`,
          "provinces"
        ).then((data) => {
          if (data?.geonames) {
            const provinceList = data.geonames.map((prov) => ({
              value: prov.name,
              label: prov.name,
            })).sort((a, b) => a.label.localeCompare(b.label));
            const idMap = {};
            data.geonames.forEach((prov) => {
              idMap[prov.name] = prov.geonameId;
            });
            setProvinces(provinceList);
            setProvinceIds(idMap);
          }
        });
      }
    }
  }, [formData.negara, rawCountries, cache]);

  useEffect(() => {
    if (formData.provinsi && formData.negara) {
      setCities([]);

      if (provinceIds[formData.provinsi]) {
        const provinceId = provinceIds[formData.provinsi];
  
        fetchWithCache(
          `${GEO_API_BASE}/children/${provinceId}?lang=en`,
          `cities_${provinceId}`,
          "cities"
        ).then((data) => {
          if (data?.geonames) {
            const cityList = data.geonames.map((city) => ({
              value: city.name,
              label: city.name,
            })).sort((a, b) => a.label.localeCompare(b.label));
            setCities(cityList);
          }
        });
      }
    }
  }, [formData.provinsi, formData.negara, provinceIds, cache]);

  const validateField = (name, value, negara = "Indonesia") => {
    const label = humanize(name);

    if (
      name !== "linkSertifikat" &&
      name !== "usernameInstagram" &&
      (value === undefined || value === null || value === "")
    ) {
      return `${label} wajib diisi`;
    }
    if (name === "namaDepan" && !/^[A-Za-z\s]{1,50}$/.test(value)) {
      return `${label} hanya boleh huruf dan spasi, maksimum 50 karakter, misalnya: "Budi"`;
    }
    if (name === "namaBelakang" && !/^[A-Za-z\s]{1,50}$/.test(value)) {
      return `${label} hanya boleh huruf dan spasi, maksimum 50 karakter, misalnya: "Santoso"`;
    }
    if (name === "telepon" && !/^\+?\d{8,15}$/.test(value)) {
      return `${label} harus 8-15 digit, boleh diawali dengan "+", misalnya: "+6281234567890"`;
    }
    if (name === "nomorIdentitas") {
      if (negara === "Indonesia") {
        if (!/^\d{16}$/.test(value)) {
          return `${label} (KTP) harus 16 digit untuk peserta dari Indonesia, misalnya: "1234567890123456"`;
        }
      } else {
        if (!/^[A-Za-z0-9]{5,20}$/.test(value)) {
          return `${label} (Paspor/Identitas) harus 5-20 karakter alfanumerik untuk peserta asing, misalnya: "AB123456"`;
        }
      }
    }
    if (
      name === "linkSertifikat" &&
      value &&
      !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(value)
    ) {
      return `${label} harus berupa URL yang valid, misalnya: "https://linksertifikatanda"`;
    }
    if (
      name === "usernameInstagram" &&
      value &&
      !/^[A-Za-z0-9._]{1,30}$/.test(value)
    ) {
      return `${label} hanya boleh huruf, angka, titik, atau garis bawah, maksimum 30 karakter, misalnya: "user_name"`;
    }
    if (name === "tanggalLahir") {
      const date = new Date(value);
      const eventDate = new Date("2025-09-27");
      const minAgeDate = new Date(
        eventDate.getFullYear() - 18,
        eventDate.getMonth(),
        eventDate.getDate()
      );
      if (isNaN(date.getTime()) || date >= eventDate) {
        return `${label} harus di masa lalu sebelum 27/9/2025, misalnya: "2005-09-27"`;
      }
      if (date > minAgeDate) {
        return `Volunteer harus berusia minimal 18 tahun pada 27/9/2025, misalnya: "2007-09-27" atau sebelumnya`;
      }
    }
    if (name === "gender" && !["Laki-laki", "Perempuan"].includes(value)) {
      return `${label} harus Laki-laki atau Perempuan`;
    }
    if (
      name === "negara" &&
      countries.length > 0 &&
      !countries.some((c) => c.value === value)
    ) {
      return `${label} harus dipilih dari daftar yang tersedia, misalnya: "Indonesia"`;
    }
    if (
      name === "provinsi" &&
      provinces.length > 0 &&
      !provinces.some((p) => p.value === value)
    ) {
      return `${label} harus dipilih dari daftar yang tersedia, misalnya: "Jawa Barat"`;
    }
    if (
      name === "kota" &&
      cities.length > 0 &&
      !cities.some((c) => c.value === value)
    ) {
      return `${label} harus dipilih dari daftar yang tersedia, misalnya: "Bandung"`;
    }
    if (name === "alamat" && !/^[A-Za-z0-9\s,.-/#()]{5,100}$/.test(value)) {
      return `${label} harus 5-100 karakter, boleh huruf, angka, spasi, koma, titik, tanda hubung, garis miring, pagar, atau tanda kurung, misalnya: "Jalan Merdeka No. 123"`;
    }
    if (
      name === "motivasiDanPengalaman" &&
      !/^[A-Za-z0-9\s,.!?-]{10,500}$/.test(value)
    ) {
      return `${label} harus 10-500 karakter, boleh huruf, angka, spasi, dan tanda baca tertentu, misalnya: "Saya pernah menjadi volunteer di lomba lari dan ingin membantu di pos aid station."`;
    }
    if (
      name === "ukuranJersey" &&
      !["XS", "S", "M", "L", "XL", "XXL"].includes(value)
    ) {
      return `${label} harus dipilih dari daftar ukuran yang tersedia, misalnya: "M"`;
    }
    if (
      name === "posisiVolunteer" &&
      ![
        "Marshalls",
        "Water Station Crew",
        "Racepack Collection Crew",
        "Race Venue Crew",
        "Medis & Rescue",
        "Photographer",
        "Check Point",
        "Logistik",
      ].includes(value)
    ) {
      return `${label} harus dipilih dari daftar posisi yang tersedia, misalnya: "Pos Aid Station"`;
    }
    return "";
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    handleChange(e);
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value, formData.negara);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      {errors.countries && (
        <p className="text-red-500 col-span-full">{errors.countries}</p>
      )}
      <InputField
        label="Nama Depan"
        name="namaDepan"
        value={formData.namaDepan || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.namaDepan && errors.namaDepan}
        labelColor={labelColor}
      />
      <InputField
        label="Nama Belakang"
        name="namaBelakang"
        value={formData.namaBelakang || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.namaBelakang && errors.namaBelakang}
        labelColor={labelColor}
      />
      <InputField
        label="Nomor Telepon/WhatsApp"
        name="telepon"
        value={formData.telepon || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.telepon && errors.telepon}
        labelColor={labelColor}
      />
      <InputField
        label="Nomor Identitas"
        name="nomorIdentitas"
        value={formData.nomorIdentitas || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.nomorIdentitas && errors.nomorIdentitas}
        labelColor={labelColor}
        placeholder={
          formData.negara === "Indonesia"
            ? "Masukkan 16 digit KTP"
            : "Masukkan nomor Paspor/Identitas"
        }
      />
      <InputField
        label="Link Sertifikat"
        name="linkSertifikat"
        value={formData.linkSertifikat || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required={false}
        error={touched.linkSertifikat && errors.linkSertifikat}
        labelColor={labelColor}
        placeholder="Link sertifikat atau dokumen pendukung lain"
      />
      <InputField
        label="Username Instagram"
        name="usernameInstagram"
        value={formData.usernameInstagram || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required={false}
        error={touched.usernameInstagram && errors.usernameInstagram}
        labelColor={labelColor}
        placeholder="Masukkan username, misalnya: user_name"
      />
      <InputField
        label="Tanggal Lahir"
        name="tanggalLahir"
        type="date"
        value={formData.tanggalLahir || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.tanggalLahir && errors.tanggalLahir}
        labelColor={labelColor}
      />
      <InputField
        label="Gender"
        name="gender"
        type="select"
        value={formData.gender || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        options={[
          { value: "", label: "Pilih Gender" },
          { value: "Laki-laki", label: "Laki-laki" },
          { value: "Perempuan", label: "Perempuan" },
        ]}
        error={touched.gender && errors.gender}
        labelColor={labelColor}
      />
      <InputField
        label="Negara"
        name="negara"
        type="select"
        value={formData.negara || "Indonesia"}
        onChange={handleFieldChange}
        disabled={isFormDisabled || loading.countries}
        options={countries}
        required
        error={touched.negara && errors.negara}
        aria-busy={loading.countries}
        labelColor={labelColor}
      />
      <InputField
        label="Provinsi"
        name="provinsi"
        type="select"
        value={formData.provinsi || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.negara || loading.provinces}
        options={[{ value: "", label: "Pilih Provinsi" }, ...provinces]}
        required
        error={touched.provinsi && errors.provinsi}
        aria-busy={loading.provinces}
        labelColor={labelColor}
      />
      <InputField
        label="Kota"
        name="kota"
        type="select"
        value={formData.kota || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.provinsi || loading.cities}
        options={[{ value: "", label: "Pilih Kota" }, ...cities]}
        required
        error={touched.kota && errors.kota}
        aria-busy={loading.cities}
        labelColor={labelColor}
      />
      <InputField
        label="Alamat"
        name="alamat"
        value={formData.alamat || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.alamat && errors.alamat}
        labelColor={labelColor}
      />
      <InputField
        label="Ukuran Jersey"
        name="ukuranJersey"
        type="select"
        value={formData.ukuranJersey || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        options={[
          { value: "", label: "Pilih Size Jersey" },
          { value: "XS", label: "XS" },
          { value: "S", label: "S" },
          { value: "M", label: "M" },
          { value: "L", label: "L" },
          { value: "XL", label: "XL" },
          { value: "XXL", label: "XXL" },
        ]}
        error={touched.ukuranJersey && errors.ukuranJersey}
        labelColor={labelColor}
      />
      <InputField
        label="Posisi Volunteer"
        name="posisiVolunteer"
        type="select"
        value={formData.posisiVolunteer || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        options={[
          { value: "", label: "Pilih Posisi Volunteer" },
          { value: "Marshalls", label: "Marshalls" },
          { value: "Water Station Crew", label: "Water Station Crew" },
          {
            value: "Racepack Collection Crew",
            label: "Racepack Collection Crew",
          },
          { value: "Race Venue Crew", label: "Race Venue Crew" },
          { value: "Medis & Rescue", label: "Medis & Rescue" },
          { value: "Photographer", label: "Photographer" },
          { value: "Check Point", label: "Check Point" },
          { value: "Logistik", label: "Logistik" },
        ]}
        error={touched.posisiVolunteer && errors.posisiVolunteer}
        labelColor={labelColor}
      />
      <div className="col-span-full">
        <InputField
          label="Motivasi dan Pengalaman Volunteer"
          name="motivasiDanPengalaman"
          type="textarea"
          value={formData.motivasiDanPengalaman || ""}
          onChange={handleFieldChange}
          disabled={isFormDisabled}
          required
          error={touched.motivasiDanPengalaman && errors.motivasiDanPengalaman}
          labelColor={labelColor}
          placeholder="Ceritakan pengalaman Anda sebagai volunteer sebelumnya dan motivasi Anda untuk bergabung!"
          className="col-span-full min-h-[240px] w-full max-w-full resize-y !important"
          style={{ minHeight: "240px" }}
        />
      </div>
    </div>
  );
};
